import { getTransactionHistory } from "../lib/hooks";
import Spinner from "./Spinner";

function fmt$(n: number) {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface TransactionTableProps {
  showUserOnly?: boolean;
}

export default function TransactionTable({ showUserOnly = true }: TransactionTableProps) {
  const { data, loading } = getTransactionHistory(showUserOnly);

  return (
    <div style={S.card}>
      <style>{`
        @media (max-width: 768px) {
          .tx-table { font-size: 11px !important; }
          .tx-th, .tx-td { padding: 8px 8px !important; }
          .tx-card { padding: 16px 12px !important; }
        }
        @media (max-width: 480px) {
          .tx-th, .tx-td { padding: 6px 6px !important; }
          .tx-table { font-size: 10px !important; }
          .tx-avatar { width: 22px !important; height: 22px !important; font-size: 10px !important; }
        }
      `}</style>
      <h3 style={S.title}>Transaction History</h3>
      {loading ? <Spinner /> : (
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={S.table} className="tx-table">
            <thead>
              <tr>
                <th style={S.th} className="tx-th">Name</th>
                <th style={S.th} className="tx-th">Date</th>
                <th style={S.th} className="tx-th">Status</th>
                <th style={S.th} className="tx-th">Invested</th>
                <th style={S.th} className="tx-th">Unit Value</th>
                <th style={S.th} className="tx-th">Total Units</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((inv, i) => {
                return (
                  <tr key={`${inv.name}-${inv.date}-${i}`} style={i % 2 === 0 ? {} : { background: "rgba(255,255,255,0.02)" }}>
                    {/* Name */}
                    <td style={S.td} className="tx-td">
                      <div style={S.nameWrap}>
                        <div style={S.avatar} className="tx-avatar">{inv.name ? inv.name[0] : "?"}</div>
                        <span style={{ fontWeight: 500 }}>{inv.name}</span>
                      </div>
                    </td>
                    
                    {/* Date */}
                    <td style={{ ...S.td, color: "var(--text-secondary)" }} className="tx-td">
                      {inv.date}
                    </td>

                    {/* Status */}
                    <td style={S.td} className="tx-td">
                      <span style={{ textTransform: "capitalize", fontWeight: 500 }}>{inv.status}</span>
                    </td>

                    {/* Invested Amount */}
                    <td style={S.td} className="tx-td">
                      {fmt$(inv.invested_amount)}
                    </td>

                    {/* Unit Value */}
                    <td style={S.td} className="tx-td">
                      {fmt$(inv.unit_value)}
                    </td>

                    {/* Total Units */}
                    <td style={S.td} className="tx-td">
                      {inv.total_units}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 16 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 550 }, // Increased minWidth slightly to prevent excessive column shrinking
  th: {
    textAlign: "left", padding: "10px 14px",
    color: "var(--text-muted)", fontSize: 11, fontWeight: 500,
    letterSpacing: "0.08em", textTransform: "uppercase",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    whiteSpace: "nowrap",
  },
  td: { padding: "12px 14px", color: "var(--text-primary)", borderBottom: "1px solid rgba(255,255,255,0.04)", whiteSpace: "nowrap" },
  nameWrap: { display: "flex", alignItems: "center", gap: 8 },
  avatar: {
    width: 28, height: 28, borderRadius: "50%",
    background: "rgba(29,158,117,0.2)", border: "1px solid var(--teal-dim)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 600, color: "var(--teal-light)", flexShrink: 0,
  },
};